<template>
  <div class="container">
    <!-- 日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。 -->
    <div>
      <md-radio
        v-for="term in selectableTermTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
      <gl-switch
        v-if="isCompareToggleVisible"
        :value="isCompareEnabled"
        @input="toggleCompare"
      >
        前期間と比較する
      </gl-switch>
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <div>
          <!-- 週の選択 -->
          <div class="container-flex" v-if="isWeekMode" key="w">
            <div v-if="isCompareEnabled" class="term-label">当期間</div>
            <gl-input-date
              class="month-select"
              v-if="weekCountFrom"
              :min="minDate"
              :max="maxDate"
              :value.sync="_weekFrom"
            />
            <gl-input-date
              class="month-select"
              v-if="weekCountTo"
              :min="minDate"
              :max="maxDate"
              :value.sync="_weekTo"
            />
            <md-field class="weekType">
              <md-select v-model="_weekType">
                <md-option
                  v-for="week in weekTypes"
                  :value="week.id"
                  :key="week.id"
                >
                  {{ week.label }}
                </md-option>
              </md-select>
            </md-field>
            <md-field class="week-count">
              <md-input
                v-model.number="weekCount"
                type="number"
                :min="1"
                :max="maxWeek"
                required
              ></md-input>
            </md-field>
            <div class="suffix">
              <span>週間</span>
            </div>
          </div>
          <!-- 日の選択 -->
          <div class="container-flex" v-if="isDateMode" key="d">
            <div v-if="isCompareEnabled" class="term-label">当期間</div>
            <gl-input-date
              class="month-select"
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateFrom"
            />
            <div class="divider">～</div>
            <gl-input-date
              class="month-select"
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateTo"
            />
          </div>
          <!-- 月の選択 -->
          <div class="container-flex" v-if="isMonthMode" key="m">
            <div v-if="isCompareEnabled" class="term-label">当期間</div>
            <gl-select-month
              class="month-select"
              :min="minDate"
              :max="maxDate"
              :value.sync="_monthFrom"
            />
            <div class="divider">～</div>
            <gl-select-month
              class="month-select"
              :min="minDate"
              :max="maxDate"
              :value.sync="_monthTo"
            />
          </div>
          <!-- 月度の選択 -->
          <div v-if="isClosingMonthMode" key="closing">
            <div class="container-flex">
              <div v-if="isCompareEnabled" class="term-label">当期間</div>
              <div>
                <div class="container-flex">
                  <gl-select-closing-month
                    class="month-select"
                    :minMonth="minClosingMonthSelectable"
                    :maxMonth="maxClosingMonth"
                    :value.sync="closingMonthFrom"
                  />
                  <div class="divider">～</div>
                  <gl-select-closing-month
                    class="month-select"
                    :minMonth="minClosingMonthSelectable"
                    :maxMonth="maxClosingMonth"
                    :value.sync="closingMonthTo"
                  />
                </div>
                <div class="container-flex">
                  <!-- 締め開始日か、締め日か -->
                  <md-field class="display-closing-month">
                    <md-select v-model="_displayClosingMonth">
                      <md-option
                        v-for="displayMonth in displayClosingMonths"
                        :value="displayMonth.id"
                        :key="displayMonth.id"
                      >
                        {{ displayMonth.label }}
                      </md-option>
                    </md-select>
                  </md-field>
                  <div class="suffix">
                    <span>表示</span>
                  </div>
                  <md-field class="week-count">
                    <md-select v-model="_closingDay">
                      <md-option
                        v-for="day in _closingDays"
                        :value="day.id"
                        :key="day.id"
                      >
                        {{ day.label }}
                      </md-option>
                    </md-select>
                  </md-field>
                  <div class="suffix">
                    <span>締め</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 前期 -->
          <div v-if="isCompareEnabled" class="container-flex">
            <div class="term-label">前期間</div>
            <md-field class="week-count">
              <md-input
                v-model.number="_termOffset"
                type="number"
                :min="1"
                :max="maxTermOffset"
                required
                @change="e => validateTermOffset(e)"
              ></md-input>
            </md-field>
            <div class="suffix">
              <span>{{ termOffsetUnitName }}前と比較</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>
<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlSelectClosingMonth from '@/components/common/GlSelectClosingMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import {
  getStartDateByWeekCount,
  getEndDateByWeekCount,
  dateLimit,
  weekMove,
  monthMove,
  toBasicDateFormat,
} from '@/util/date'
import ErrorReport from '@/mixins/ErrorReport'
import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'
// 通常（このコンポーネントへの入力や出力）で利用する日付フォーマット
const DATE_FORMAT = 'YYYYMMDD'
const MONTH_FORMAT = 'YYYYMM'

/**
 * 期間選択コンポーネント
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  mixins: [ErrorReport, QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [
        { id: 'w', label: '週' },
        { id: 'm', label: '月' },
        { id: 'd', label: '日' },
        { id: 'closing', label: '月度' },
      ],
      weekTypes: [
        { id: 'COUNT_FROM', label: 'から' },
        { id: 'COUNT_TO', label: 'まで' },
      ],
      closingDays: [...Array(28)].map((m, idx) => {
        return { id: idx + 1, label: `${idx + 1}日` }
      }),
      displayClosingMonths: [
        { id: 'DISPLAY_FROM', label: '月初日' },
        { id: 'DISPLAY_TO', label: '月末日' },
      ],
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  components: { GlSelectMonth, GlSelectClosingMonth, GlInputDate },
  props: {
    // 選択している選択種別（日 / 週 / 月 / 月度）
    termType: {
      type: String,
      default: 'w',
    },
    base: {
      dateFrom: {
        type: String,
        validator(value) {
          const regex = new RegExp(/^\d{8}$/g)
          return regex.test(value)
        },
      },
      dateTo: {
        type: String,
        validator(value) {
          const regex = new RegExp(/^\d{8}$/g)
          return regex.test(value)
        },
      },
      // 選択している選択種別（から / まで）
      weekType: {
        type: String,
        default: 'COUNT_FROM',
      },
    },
    compare: {
      dateFrom: {
        type: String,
        validator(value) {
          const regex = new RegExp(/^\d{8}$/g)
          return regex.test(value)
        },
      },
      dateTo: {
        type: String,
        validator(value) {
          const regex = new RegExp(/^\d{8}$/g)
          return regex.test(value)
        },
      },
      // 選択している選択種別（から / まで）
      weekType: {
        type: String,
        default: 'COUNT_FROM',
      },
    },
    // 選択している選択種別（から / まで）
    weekType: {
      type: String,
      default: 'COUNT_FROM',
    },
    /**
     * 選択可能な期間種別
     */
    visibleTermTypes: {
      type: Array,
      default: () => ['w', 'm', 'd', 'closing'],
    },
    // 選択している表示月（月初日 / 月末日）
    displayClosingMonth: {
      type: String,
      default: 'DISPLAY_TO',
    },
    // 締め日（月度選択時）
    closingDay: {
      type: Number,
      default: 20,
    },
    /**
     * 期間比較を行うか
     */
    isCompareEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 比較期間の数
     */
    termOffset: {
      type: Number,
      default: 5,
    },
    // 以下はtermPos.jsで管理していないので、各コンポーネントで定義の必要あり
    /**
     * 期間比較を行うか
     */
    isCompareToggleVisible: {
      type: Boolean,
      default: true,
    },
    /**
     * 週数の初期値
     */
    defaultWeekCount: {
      type: Number,
      default: 13,
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     */
    maxDate() {
      this.adjustRangeIfNeeded()
    },
    minDate() {
      this.adjustRangeIfNeeded()
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     *     2019/01/14～2019/05/03 -> 2019/01(2019/01/01)～2019/05(2019/05/31)
     */
    termType(value) {
      if (this.selectableTermTypes.every(m => m.id !== value)) {
        value = 'w'
      }
      this.$emit('update:termType', value)
      let baseDateFrom = this.base.dateFrom
      let baseDateTo = this.base.dateTo
      let compareDateFrom = this.compare.dateFrom
      let diffCount = 0
      // dateFrom, dateToはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateFrom`, `update:dateTo` をemitするだけです。
      switch (value) {
        case 'w':
          // 週の場合終了日は表示されませんが、内部的には開始日からx週間後の日付を保持します。
          baseDateTo = getEndDateByWeekCount(baseDateFrom, this.weekCount)
          baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
          this.$emit('update:base', {
            ...this.base,
            dateFrom: baseDateFrom,
            dateTo: baseDateTo,
          })
          // 差週数を取得
          diffCount = Math.ceil(
            moment(baseDateFrom).diff(moment(compareDateFrom), 'days', true) /
              7,
          )
          diffCount = diffCount > this.maxTermOffset ? diffCount - 1 : diffCount
          this.$emit('update:termOffset', diffCount)
          break
        case 'm':
          baseDateFrom = moment(baseDateFrom)
            .startOf('month')
            .format('YYYYMMDD')
          baseDateTo = moment(baseDateTo)
            .endOf('month')
            .format('YYYYMMDD')
          baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
          baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
          this.$emit('update:base', {
            ...this.base,
            dateFrom: baseDateFrom,
            dateTo: baseDateTo,
          })
          // 差月数の取得
          diffCount = moment(baseDateFrom).diff(
            moment(compareDateFrom),
            'months',
            true,
          )
          diffCount =
            diffCount > this.maxTermOffset
              ? Math.floor(diffCount)
              : Math.ceil(diffCount)
          this.$emit('update:termOffset', diffCount)
          // 前期間を設定する。
          this.$nextTick(() => {
            this.setCompareDate(diffCount)
          })
          break
        case 'd':
          // termOffsetの算出だけ行う
          this.$emit(
            'update:termOffset',
            moment(baseDateFrom).diff(moment(compareDateFrom), 'days'),
          )
          break
        case 'closing':
          baseDateFrom = moment(baseDateFrom).date(this.closingDay + 1)
          baseDateTo = moment(baseDateTo).date(this.closingDay)
          // 開始日・終了日の月が同じになってしまう場合は開始日を1ヵ月前にずらす
          if (baseDateFrom.month() === baseDateTo.month()) {
            baseDateFrom.subtract(1, 'months')
          }
          baseDateFrom = dateLimit(
            baseDateFrom.format(DATE_FORMAT),
            this.minDate,
            this.maxDate,
          )
          baseDateTo = dateLimit(
            baseDateTo.format(DATE_FORMAT),
            this.minDate,
            this.maxDate,
          )

          this.$emit('update:base', {
            ...this.base,
            dateFrom: baseDateFrom,
            dateTo: baseDateTo,
          })
          // 差月数の取得
          diffCount =
            (moment(baseDateFrom).year() - moment(compareDateFrom).year()) *
              12 +
            (moment(baseDateFrom).month() - moment(compareDateFrom).month())
          this.$emit('update:termOffset', diffCount)
          // 前期間を設定する。
          this.$nextTick(() => {
            this.setCompareDate(diffCount)
          })
          break
        default:
          break
      }
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },
    /**
     * 選択可能なTermTypeを取得
     */
    selectableTermTypes() {
      return this.termTypes.filter(t => this.visibleTermTypes.includes(t.id))
    },
    /**
     * ラジオボタンで押された期間のtermTypeを変更します
     */
    _termType: {
      get() {
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    /**
     *TermTypeによって表示する単位名を取得
     */
    termOffsetUnitName() {
      switch (this._termType) {
        case 'm':
          return 'ヵ月'
        case 'w':
          return '週間'
        case 'd':
          return '日'
        case 'closing':
          return 'ヵ月'
      }
      return '日'
    },
    maxTermOffset() {
      let ret = 0
      switch (this._termType) {
        case 'd':
          ret = moment(this.base.dateFrom).diff(this.minDate, 'days')
          break
        case 'w':
          ret = moment(this.base.dateFrom).diff(this.minDate, 'week')
          break
        case 'm':
          ret = moment(this.base.dateFrom).diff(this.minDate, 'month')
          break
        case 'closing':
          ret = moment(this.base.dateFrom).diff(this.minDate, 'month')
          break
        default:
          ret = moment(this.base.dateFrom).diff(this.minDate, 'days')
          break
      }
      return ret
    },
    _termOffset: {
      get() {
        return this.termOffset
      },
      set(newVal) {
        if (!newVal) {
          newVal = 0
        }
        this.setCompareDate(newVal)
        this.$emit('update:termOffset', newVal)
      },
    },
    // ===============================
    // 📆 日選択用のロジック
    // ===============================
    isDateMode() {
      return this.termType === 'd'
    },
    // このコンポーネントの入出力となる開始・終了日のフォーマットと、
    // inputに設定する値の日付フォーマットが異なるので変換用computedプロパティを用意
    /**
     * 開始日
     */
    _dateFrom: {
      get() {
        return this.base.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:base', { ...this.base, dateFrom: newVal })
        this.$nextTick(() => {
          this.setCompareDate(this._termOffset)
        })
      },
    },
    /**
     * 終了日
     */
    _dateTo: {
      get() {
        return this.base.dateTo
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:base', { ...this.base, dateTo: newVal })
        this.$nextTick(() => {
          this.setCompareDate(this._termOffset)
        })
      },
    },
    // ===============================
    // 📆 週選択用のロジック
    // ===============================
    isWeekMode() {
      return this.termType === 'w'
    },
    weekCountFrom() {
      return this.weekType === 'COUNT_FROM'
    },
    weekCountTo() {
      return this.weekType === 'COUNT_TO'
    },
    maxWeek() {
      if (this.weekCountFrom) {
        // から
        return moment(this.maxDate).diff(this.base.dateFrom, 'weeks') + 1
      } else {
        // まで
        return moment(this.base.dateTo).diff(this.minDate, 'weeks') + 1
      }
    },
    /**
     * ～から、～までの選択肢が変更された際weekTypeを変更します
     */
    _weekType: {
      get() {
        return this.weekType
      },
      set(newVal) {
        this.$emit('update:base', {
          ...this.base,
          weekType: newVal,
        })
        this.$emit('update:compare', {
          ...this.compare,
          weekType: newVal,
        })
        this.$emit('update:weekType', newVal)
      },
    },
    /**
     * termTypeが週で開始日が変更された場合ここで変更保存をしています。
     * x週間の間隔を保つため終了日も一緒に変更保存。
     */
    _weekFrom: {
      get() {
        return this.base.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        let dateTo = weekMove(this.base.dateTo, this.base.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:base', {
          ...this.base,
          dateFrom: newVal,
          dateTo: dateTo,
        })
        const compareDateFrom = moment(newVal)
          .subtract(this._termOffset, 'weeks')
          .format(DATE_FORMAT)
        const compareDateTo = moment(dateTo)
          .subtract(this._termOffset, 'week')
          .format(DATE_FORMAT)
        this.$emit('update:compare', {
          ...this.compare,
          dateFrom: compareDateFrom,
          dateTo: compareDateTo,
        })
      },
    },
    _weekTo: {
      get() {
        return this.base.dateTo
      },
      set(newVal) {
        let baseDateFrom = weekMove(
          this.base.dateFrom,
          this.base.dateTo,
          newVal,
        )
        baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:base', {
          ...this.base,
          dateFrom: baseDateFrom,
          dateTo: newVal,
        })
        const compareDateTo = moment(newVal)
          .subtract(this._termOffset, 'weeks')
          .format(DATE_FORMAT)
        const compareDateFrom = moment(baseDateFrom)
          .subtract(this._termOffset, 'week')
          .format(DATE_FORMAT)
        this.$emit('update:compare', {
          ...this.compare,
          dateFrom: compareDateFrom,
          dateTo: compareDateTo,
        })
      },
    },
    /**
     * termTypeが週で週数が変更された場合ここで変更保存をしています
     * 内部的に日付で期間を管理している為、週数と終了日をemitします
     * weekType（から、まで）によって処理を変えている
     */
    weekCount: {
      get() {
        return (
          moment(this.base.dateTo).diff(moment(this.base.dateFrom), 'weeks') + 1
        )
      },
      set(newVal) {
        if (this.weekCountFrom) {
          let baseDateFrom = this.base.dateFrom
          let baseDateTo = getEndDateByWeekCount(baseDateFrom, newVal)
          baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
          this.$emit('update:base', {
            ...this.base,
            dateTo: baseDateTo,
          })
          let compareDateTo = moment(baseDateTo)
            .subtract(this._termOffset, 'weeks')
            .format(DATE_FORMAT)
          compareDateTo = dateLimit(compareDateTo, this.minDate, this.maxDate)
          this.$emit('update:compare', {
            ...this.compare,
            dateTo: compareDateTo,
          })
        } else if (this.weekCountTo) {
          let baseDateTo = this.base.dateTo
          let baseDateFrom = getStartDateByWeekCount(baseDateTo, newVal)
          baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
          this.$emit('update:base', {
            ...this.base,
            dateFrom: baseDateFrom,
          })
          let compareDateFrom = moment(baseDateFrom)
            .subtract(this._termOffset, 'weeks')
            .format(DATE_FORMAT)
          compareDateFrom = dateLimit(
            compareDateFrom,
            this.minDate,
            this.maxDate,
          )
          this.$emit('update:compare', {
            ...this.compare,
            dateFrom: compareDateFrom,
          })
        }
      },
    },
    // ===============================
    // 📆 月選択用のロジック
    // ===============================
    isMonthMode() {
      return this.termType === 'm'
    },
    /**
     * termTypeが月
     */
    _monthFrom: {
      get() {
        return moment(this.base.dateFrom).format('YYYYMM')
      },
      set(newVal) {
        let baseDateFrom = moment(newVal)
          .startOf('month')
          .format(DATE_FORMAT)
        baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
        let compareDateFrom = monthMove(
          this.compare.dateFrom,
          this.base.dateFrom,
          baseDateFrom,
        )
        compareDateFrom = dateLimit(compareDateFrom, this.minDate, this.maxDate)

        this.$emit('update:base', { ...this.base, dateFrom: baseDateFrom })
        this.$emit('update:compare', {
          ...this.compare,
          dateFrom: compareDateFrom,
        })
      },
    },
    /**
     * termTypeが月
     */
    _monthTo: {
      get() {
        return moment(this.base.dateTo).format('YYYYMM')
      },
      set(newVal) {
        let baseDateTo = moment(newVal)
          .endOf('month')
          .format(DATE_FORMAT)
        baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
        let compareDateTo = monthMove(
          this.compare.dateTo,
          this.base.dateTo,
          baseDateTo,
        )
        compareDateTo = dateLimit(compareDateTo, this.minDate, this.maxDate)

        this.$emit('update:base', { ...this.base, dateTo: baseDateTo })
        this.$emit('update:compare', { ...this.compare, dateTo: compareDateTo })
      },
    },
    // ===============================
    // 📆 月度選択用のロジック
    // ===============================
    isClosingMonthMode() {
      return this.termType === 'closing'
    },
    /**
     * 月度指定で選択可能な最小年月を算出
     */
    minClosingMonthSelectable() {
      // 月度の場合は締め日表示に指定している場合は、開始月を指定できるのはおかしいことになってしまうので、制御
      const defaultMinDate = this.minDate
      let addMonths = 0
      // 締め日月表示の場合、確実に先月のいずれかの日付が開始日になるため、最小月から1つ増やす
      if (this.displayClosingMonthIsTo) {
        addMonths += 1
      }
      // MinDate：2025/05/25で締め日が20日の場合、
      // 2025/05/21~2025/06/20の期間の月度は選択できないようにするため、
      // 締め日より最小日が大きい場合は1つ増やす。
      if (moment(defaultMinDate).date() > this.closingDay + 1) {
        addMonths += 1
      }
      // 期間比較の場合は前期間分の日付も追加しないとなので、最低1ヵ月を追加する
      if (this.isCompareEnabled) {
        addMonths += 1
      }
      // MinDate：2025/5/25 で締め日が20日、表示月度は締め日月の場合、
      // 2025年7月を最小月とする（2025/6/21~2025/7/20の期間が最小）
      // 2025/5/21~2025/6/20は選択不可とする
      const monthMinDate = moment(defaultMinDate)
        .month(moment(defaultMinDate).month() + addMonths)
        .format(MONTH_FORMAT)
      return monthMinDate
    },
    /**
     * 月度指定で締め日を無視したら選択可能な最小年月
     */
    minClosingMonth() {
      // 月度の場合は締め日表示に指定している場合は、開始月を指定できるのはおかしいことになってしまうので、制御
      const defaultMinDate = this.minDate
      let addMonths = 0
      // 締め日月表示の場合、確実に先月のいずれかの日付が開始日になるため、最小月から1つ増やす
      if (this.displayClosingMonthIsTo) {
        addMonths += 1
      }
      // 期間比較の場合は前期間分の日付も追加しないとなので、最低1ヵ月を追加する
      if (this.isCompareEnabled) {
        addMonths += 1
      }
      // MinDate：2025/5/25 で締め日が20日、表示月度は締め日月の場合、
      // 2025年7月を最小月とする（2025/6/21~2025/7/20の期間が最小）
      // 2025/5/21~2025/6/20は選択不可とする
      const monthMinDate = moment(defaultMinDate)
        .month(moment(defaultMinDate).month() + addMonths)
        .format(MONTH_FORMAT)
      return monthMinDate
    },
    maxClosingMonth() {
      // @memo もっと簡略化できたけど、minClosingMonthに合わせた記述にしました。
      // 月度の場合は開始日表示に指定している場合は、最終月を指定できるのはおかしいことになってしまうので、制御
      const defaultMaxDate = this.maxDate
      let divideMonths = 0
      // 開始日月表示の場合、確実に次月のいずれかの日付が開始日になるため、最大月から1つ減らす
      if (!this.displayClosingMonthIsTo) {
        divideMonths += 1
      }

      // MaxDate：2025/11/15 で締め日が20日、表示月度は開始日月の場合、
      // 2025年9月を最大月とする（2025/9/21~2025/10/20の期間が最小）
      // 2025/10/21~2025/11/20は選択不可とする
      const monthMaxDate = moment(defaultMaxDate)
        .month(moment(defaultMaxDate).month() - divideMonths)
        .format(MONTH_FORMAT)
      return monthMaxDate
    },
    /**
     * 締め日は最小日、最大日からはみ出る日付は選択不可とする。
     * （開始月が最小月、終了月が最大月の場合に限る）
     */
    _closingDays() {
      let ret = this.closingDays
      if (this.closingMonthTo === this.maxClosingMonth) {
        ret = ret.filter(f => f.id <= moment(this.maxDate).date())
      }
      if (this.closingMonthFrom === this.minClosingMonth) {
        ret = ret.filter(f => f.id + 1 >= moment(this.minDate).date())
      }
      return ret
    },
    /**
     * 月度の表示が締め日基準かどうか
     */
    displayClosingMonthIsTo() {
      // 締め日側の月を月度と表示する
      return this._displayClosingMonth === 'DISPLAY_TO'
    },
    /**
     * 締め日
     */
    _closingDay: {
      get() {
        return this.closingDay
      },
      set(newVal) {
        this.$emit('update:closingDay', newVal)
        const dateFrom = toBasicDateFormat(this.closingMonthFrom)
        const dateTo = toBasicDateFormat(this.closingMonthTo)

        this.setDateByClosingMonth(dateFrom, dateTo, newVal)

        this.$nextTick(() => {
          this.setCompareDate(this._termOffset)
        })
      },
    },
    /**
     * 開始月度
     */
    closingMonthFrom: {
      get() {
        if (moment(this.base.dateFrom).date() == 1) {
          return moment(this.base.dateFrom)
            .month(moment(this.base.dateFrom).month() - 1)
            .format('YYYYMM')
        } else {
          if (this.displayClosingMonthIsTo) {
            return moment(this.base.dateFrom)
              .month(moment(this.base.dateFrom).month() + 1)
              .format('YYYYMM')
          } else {
            return moment(this.base.dateFrom).format('YYYYMM')
          }
        }
      },
      set(newVal) {
        let baseDateFrom = toBasicDateFormat(newVal)
        let baseDateTo = toBasicDateFormat(this.closingMonthTo)
        // ここで、月度の表示月によって開始日を設定する
        this.setDateByClosingMonth(baseDateFrom, baseDateTo, this.closingDay)

        this.$nextTick(() => {
          this.setCompareDate(this._termOffset)
        })
      },
    },
    /**
     * 終了月度
     */
    closingMonthTo: {
      get() {
        if (this.displayClosingMonthIsTo) {
          return moment(this.base.dateTo).format('YYYYMM')
        } else {
          return moment(this.base.dateTo)
            .month(moment(this.base.dateTo).month() - 1)
            .format('YYYYMM')
        }
      },
      set(newVal) {
        let baseDateFrom = toBasicDateFormat(this.closingMonthFrom)
        let baseDateTo = toBasicDateFormat(newVal)

        this.setDateByClosingMonth(baseDateFrom, baseDateTo, this.closingDay)

        this.$nextTick(() => {
          this.setCompareDate(this._termOffset)
        })
      },
    },
    /**
     * 月初日、月末日の選択肢が変更された際displayClosingMonthを変更します
     */
    _displayClosingMonth: {
      get() {
        return this.displayClosingMonth
      },
      set(newVal) {
        this.$emit('update:displayClosingMonth', newVal)
      },
    },
    errors() {
      const errors = []
      if (this.isCompareEnabled) {
        if (this.maxTermOffset <= 0) {
          errors.push(
            '前期間を選択できないため、当期間を後ろにずらしてください',
          )
        } else if (!this._termOffset) {
          errors.push(
            `前期間は1~${this.maxTermOffset}${
              this.termOffsetUnitName
            }の範囲で入力してください`,
          )
        } else if (
          this._termOffset > this.maxTermOffset ||
          this._termOffset <= 0
        ) {
          errors.push(
            `前期間は1~${this.maxTermOffset}${
              this.termOffsetUnitName
            }の範囲で入力してください`,
          )
        }
      }
      if (this.base.dateFrom && this.base.dateTo) {
        if (this.base.dateFrom > this.base.dateTo) {
          errors.push('終了期間は開始期間以降を選択してください')
        }
      } else {
        errors.push('期間を選択してください')
      }
      return errors
    },
  },
  async created() {
    let baseDateFrom = this.base.dateFrom
    let baseDateTo = this.base.dateTo
    let compareDateFrom = this.compare.dateFrom
    let compareDateTo = this.compare.dateTo
    // 検索期間の初期値指定、任意の初期値がある場合はelseへ
    // 検索期限は外部APIから取得する必要がある為、ここに記述
    // 当期
    if (!baseDateFrom || !baseDateTo) {
      baseDateFrom = moment(this.maxDate)
        .subtract(this.defaultWeekCount, 'week')
        .add(1, 'days')
        .format(DATE_FORMAT)
      baseDateTo = this.maxDate
      // 検索期間が検索期限を超えていた場合の処理
      baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
      baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
      this.$emit('update:base', {
        dateFrom: baseDateFrom,
        dateTo: baseDateTo,
        weekType: this.base.weekType,
      })
    }
    // 前期
    if (!compareDateFrom || !compareDateTo) {
      compareDateFrom = moment(this.maxDate)
        .subtract(this.defaultWeekCount, 'week')
        .add(1, 'days')
        .format(DATE_FORMAT)
      compareDateTo = this.maxDate
      // 検索期間が検索期限を超えていた場合の処理
      compareDateFrom = dateLimit(compareDateFrom, this.minDate, this.maxDate)
      compareDateTo = dateLimit(compareDateTo, this.minDate, this.maxDate)
      this.$emit('update:compare', {
        dateFrom: compareDateFrom,
        dateTo: compareDateTo,
        weekType: this.compare.weekType,
      })
    }
  },
  methods: {
    toggleCompare(value) {
      this.$emit('update:isCompareEnabled', value)
      this.setCompareDate(this._termOffset)
    },
    /**
     * 月度を指定している際のdateFrom, dateToのセットを行う
     */
    setDateByClosingMonth(dateFrom, dateTo, closingDay) {
      let baseDateFrom = moment(dateFrom)
      if (this.displayClosingMonthIsTo) {
        baseDateFrom = baseDateFrom
          .month(moment(baseDateFrom).month() - 1)
          .date(closingDay + 1)
          .format(DATE_FORMAT)
      } else {
        baseDateFrom = baseDateFrom.date(closingDay + 1).format(DATE_FORMAT)
      }
      baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)

      let baseDateTo = moment(dateTo)
      if (this.displayClosingMonthIsTo) {
        baseDateTo = baseDateTo.date(closingDay).format(DATE_FORMAT)
      } else {
        baseDateTo = baseDateTo
          .month(baseDateTo.month() + 1)
          .date(closingDay)
          .format(DATE_FORMAT)
      }
      baseDateTo = dateLimit(baseDateTo, this.minDate, this.maxDate)
      // ここで、Max期限日を超えている場合は締め日を最大日に戻す。
      if (closingDay > moment(baseDateTo).date()) {
        this.$emit('update:closingDay', moment(baseDateTo).date())
      }
      // {
      //   締め日がmaxDateに合う(ロジックで) &
      //   DateFromが最小年月日に合わせられている &
      //   DateFromの日付が締め日+1日の日になっていない(締め日が最小年月日の範囲外)
      // }
      // 上記の場合は最小を1ヵ月度プラスする。（前期間と比較時はさらに1ヵ月プラス）
      if (
        closingDay + 1 !== moment(baseDateFrom).date() ||
        (this.isCompareEnabled &&
          moment(baseDateFrom)
            .subtract(1, 'months')
            .diff(moment(this.minDate)) < 0)
      ) {
        baseDateFrom = moment(baseDateFrom)
          .add(1, 'months')
          .date(closingDay + 1)
          .format(DATE_FORMAT)
        baseDateFrom = dateLimit(baseDateFrom, this.minDate, this.maxDate)
      }
      this.$emit('update:base', {
        ...this.base,
        dateFrom: baseDateFrom,
        dateTo: baseDateTo,
      })
    },
    // 前期間を算出してセットする
    setCompareDate(offsetValue) {
      let compareDateFrom = this.compare.dateFrom
      let compareDateTo = this.compare.dateTo
      let offsetVal = offsetValue
      if (!this.isCompareEnabled) {
        offsetVal = 0
        this.$emit('update:termOffset', 0)
      }
      switch (this._termType) {
        case 'd':
          compareDateFrom = moment(this.base.dateFrom).subtract(
            offsetVal,
            'days',
          )
          compareDateTo = moment(this.base.dateTo).subtract(offsetVal, 'days')
          break
        case 'w':
          compareDateFrom = moment(this.base.dateFrom).subtract(
            offsetVal,
            'week',
          )
          compareDateTo = moment(this.base.dateTo).subtract(offsetVal, 'weeks')
          break
        case 'm':
          compareDateFrom = moment(this.base.dateFrom).subtract(
            offsetVal,
            'month',
          )
          compareDateTo = moment(this.base.dateTo)
            .subtract(offsetVal, 'months')
            .endOf('month')
          break
        case 'closing':
          compareDateFrom = moment(this.base.dateFrom).subtract(
            offsetVal,
            'month',
          )
          compareDateFrom = moment(compareDateFrom)
            .date(this.closingDay + 1)
            .format(DATE_FORMAT)
          compareDateTo = monthMove(
            compareDateFrom,
            this.base.dateFrom,
            this.base.dateTo,
          )
          compareDateTo = moment(compareDateTo)
            .date(this.closingDay)
            .format(DATE_FORMAT)
          break
        default:
          compareDateFrom = moment(this.base.dateFrom).subtract(
            offsetVal,
            'days',
          )
          compareDateTo = moment(this.base.dateTo).subtract(offsetVal, 'days')
          break
      }
      compareDateFrom = moment(compareDateFrom).format(DATE_FORMAT)
      compareDateFrom = dateLimit(compareDateFrom, this.minDate, this.maxDate)
      compareDateTo = moment(compareDateTo).format(DATE_FORMAT)
      compareDateTo = dateLimit(compareDateTo, this.minDate, this.maxDate)
      this.$emit('update:compare', {
        ...this.compare,
        dateFrom: compareDateFrom,
        dateTo: compareDateTo,
      })
    },
    validateTermOffset(e) {
      let newVal = e.target.value
      let parsed = Number(newVal)
      if (isNaN(parsed)) parsed = 1
      if (parsed > this.maxTermOffset) {
        parsed = this.maxTermOffset
      }
      if (parsed <= 1) {
        parsed = 1
      }
      this.$emit('update:termOffset', parsed)
    },
    adjustRangeIfNeeded() {
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const baseDateFromAdjusted = dateLimit(
        this.base.dateFrom,
        this.minDate,
        this.maxDate,
      )
      const baseDateToAdjusted = dateLimit(
        this.base.dateTo,
        this.minDate,
        this.maxDate,
      )
      const compareDateFromAdjusted = dateLimit(
        this.compare.dateFrom,
        this.minDate,
        this.maxDate,
      )
      const compareDateToAdjusted = dateLimit(
        this.compare.dateTo,
        this.minDate,
        this.maxDate,
      )

      if (
        baseDateFromAdjusted !== this.base.dateFrom ||
        baseDateToAdjusted !== this.base.dateTo
      ) {
        this.$emit('update:base', {
          ...this.base,
          dateFrom: baseDateFromAdjusted,
          dateTo: baseDateToAdjusted,
        })
        this.snackbar.message =
          '(対象期間)指定期間が選択可能な期間外だったため、自動調整しました。'
        this.snackbar.show = true
      }

      if (
        compareDateFromAdjusted !== this.compare.dateFrom ||
        compareDateToAdjusted !== this.compare.dateTo
      ) {
        this.snackbar.message =
          '(比較期間)指定期間が選択可能な期間外です。前期間の指定を見直してください。'
        this.snackbar.show = true
      }
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}
.month-select {
  width: 160px;
}
.week-count {
  width: 60px;
}
.display-closing-month {
  width: 70px;
}
.container-flex {
  display: flex;
}
.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
  width: 20px;
}
.term-label {
  align-items: center;
  display: flex;
  justify-content: flex-start;
  margin-right: 32px;
  white-space: nowrap;
  width: 40px;
  box-sizing: border-box;
}

.suffix {
  align-items: center;
  display: flex;
  justify-content: flex-start;
  margin-left: 16px;
  margin-right: 32px;
  white-space: nowrap;
  width: 40px;
}
.weekType {
  width: 60px;
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
}

// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}
</style>
